import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography, Zoom } from '@mui/material';
import moment from 'moment';
import Constants from 'style/constants';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { splitFullNameToInitials } from 'shared/utils/string-utils';

import styles from 'features/temporary-access/views/temporary-access-table.module.scss';
import TemporaryAccess from '../domain/models/temporary-access';
import { TemporaryAccesStatus } from '../domain/models/temporary-access-status';
import { TemporaryAccessType } from '../domain/models/temporary-access-type';
import { useTemporaryAccessContextProvider } from '../providers/temporary-access-provider';

function TemporaryAccessTable(): ReactElement {
  const { t } = useTranslation('temporaryAccess');

  const {
    readTemporaryAccessData,
    readTemporaryAccessIsFetching,
    readTemporaryAccessIsSuccess,
    revokeTemporaryAccess,
    temporaryAccessFilterHook,
    selectTemporaryUser,
  } = useTemporaryAccessContextProvider();

  const overflowMenuOptions = [
    {
      label: t('temporaryAccessTable.action.read'),
      action: (temporaryAccess: TemporaryAccess) => selectTemporaryUser(temporaryAccess.id, 'viewing'),
      isVisible: (temporaryAccess: TemporaryAccess) =>
        temporaryAccess.status === TemporaryAccesStatus.Active && temporaryAccess.type === TemporaryAccessType.User,
    },
    {
      label: t('temporaryAccessTable.action.revoke'),
      action: (temporaryAccess: TemporaryAccess) => revokeTemporaryAccess(temporaryAccess),
    },
  ] as TableOverflowMenuItem<TemporaryAccess>[];

  const columns: ColumnDefinition<TemporaryAccess, NestedKeyof<TemporaryAccess>>[] = [
    {
      key: 'fullName',
      label: t('temporaryAccessTable.column.name'),
      renderCustomContentProvider: (item) => <Typography variant="subtitle1">{item.fullName}</Typography>,
      disableSort: true,
    },
    {
      key: 'type',
      label: t('temporaryAccessTable.column.type'),
      renderCustomContentProvider: (item) => <Typography variant="subtitle1">{t(`type.${item.type}`)}</Typography>,
      disableSort: true,
    },
    {
      key: 'organisationUnitWithAncestors',
      label: t('temporaryAccessTable.column.organisationUnit'),
      renderCustomContentProvider: (item) => (
        <div className={styles.breadCrumbContainer}>
          {item.organisationUnitWithAncestors.map((organisationUnit) => {
            return (
              <BreadCrumb
                key={`temporary-users-breadcrumb-${organisationUnit.id}`}
                showEllipsis={organisationUnit.ancestors.length > 2}
                allElementNames={organisationUnit.ancestors.map((name) => name)}
                data-testid={`temporary-users-breadcrumb-child-${item.id}`}
              >
                {organisationUnit.ancestors.map((name, index) => {
                  return (
                    <Typography
                      data-testid={`temporary-users-breadcrumb-child-${index}`}
                      key={`temporary-users-breadcrumb-child-${name}`}
                      variant="caption"
                      className="bread-crumb-item"
                    >
                      {name}
                    </Typography>
                  );
                })}
              </BreadCrumb>
            );
          })}
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'startsOn',
      label: t('temporaryAccessTable.column.accessStartsOn'),
      renderCustomContentProvider: (item) => <>{moment(item.startsOn).format('DD-MM-YYYY').toString()}</>,
      disableSort: true,
    },
    {
      key: 'endsOn',
      label: t('temporaryAccessTable.column.accessEndsOn'),
      renderCustomContentProvider: (item) => <>{item.endsOn && moment(item.endsOn).format('DD-MM-YYYY').toString()}</>,
      disableSort: true,
    },
    {
      key: 'temporaryChannelAccessDetails',
      label: t('temporaryAccessTable.column.reason'),
      renderCustomContentProvider: (item) => (
        <>
          {item.temporaryChannelAccessDetails !== null && item.temporaryChannelAccessDetails?.reasonType && (
            <Tooltip title={item.temporaryChannelAccessDetails?.reasonText} followCursor slots={{ transition: Zoom }}>
              <Typography variant="subtitle1">{t(`reasonType.${item.temporaryChannelAccessDetails?.reasonType}`)}</Typography>
            </Tooltip>
          )}
        </>
      ),
      disableSort: true,
    },
    {
      key: 'temporaryUserDetails',
      label: t('temporaryAccessTable.column.invitedBy'),
      renderCustomContentProvider: (item) => (
        <>
          {item.temporaryUserDetails !== null && item.temporaryUserDetails?.createdBy !== '' && (
            <Tooltip title={item.temporaryUserDetails?.createdBy} followCursor slots={{ transition: Zoom }}>
              <div className={styles.avatarContainer}>
                <Typography variant="overline" sx={{ color: Constants.Colors.onSurfaceVariant }}>
                  {splitFullNameToInitials(item.temporaryUserDetails?.createdBy)}
                </Typography>
              </div>
            </Tooltip>
          )}
        </>
      ),
      disableSort: true,
    },
    {
      key: 'status',
      label: '',
      renderCustomContentProvider: (item) => (
        <Tooltip
          title={
            item.status === TemporaryAccesStatus.Revoked
              ? t('revokedBy', {
                  username: item.revokedBy,
                  date: moment(item.revokedOn).format('DD-MM-YYYY HH:mm').toString(),
                })
              : ''
          }
          followCursor
          slots={{ transition: Zoom }}
        >
          <div className={`${styles.temporaryUsersStatus} ${item.status === TemporaryAccesStatus.Active ? styles.active : ''}`}>
            {t(`status.${item.status}`)}
          </div>
        </Tooltip>
      ),
      disableSort: true,
    },
  ];

  return (
    <>
      {readTemporaryAccessIsFetching && <LoadingIndicator />}
      {readTemporaryAccessIsSuccess && readTemporaryAccessData && (
        <Table
          data={readTemporaryAccessData.temporaryAccess.map<RowItem<TemporaryAccess>>((temporaryAccess) => ({
            isHighlighted: temporaryAccess.status === TemporaryAccesStatus.Active,
            hideOverflowMenuOptions: temporaryAccess.status !== TemporaryAccesStatus.Active,
            data: temporaryAccess,
            isClickable: temporaryAccess.status === TemporaryAccesStatus.Active && temporaryAccess.type === TemporaryAccessType.User,
          }))}
          columns={columns}
          pagination={{
            count: readTemporaryAccessData.total,
            page: temporaryAccessFilterHook.tableHook.currentPage,
            rowsPerPage: temporaryAccessFilterHook.tableHook.currentRowsPerPage,
            onPageChanged: temporaryAccessFilterHook.tableHook.handleOnPageChanged,
            onRowsPerPageChanged: temporaryAccessFilterHook.tableHook.handleOnRowsPerPageChanged,
          }}
          rowIdentifier={(item) => item.id}
          overflowMenuOptions={overflowMenuOptions}
          onItemClick={(temporaryAccess) => selectTemporaryUser(temporaryAccess.id, 'viewing')}
        />
      )}
    </>
  );
}

export default TemporaryAccessTable;
